import { useAppDispatch, useAppSelector } from '@core/hooks/redux-hooks';
import { EmptyValues } from '@app/shared/constants/empty-values';
import { NavigationItem } from '@app/shared/components/secondary-navigation/secondary-navigation-types';
import { SecondaryNavigation } from '@app/shared/components/secondary-navigation/SecondaryNavigation';
import { useParams, useSearchParams } from 'react-router-dom';
import { useEffect, useMemo, useRef } from 'react';
import { SecondaryNavigationVariation } from '@app/shared/components/secondary-navigation/secondary-navigation-enums';
import PwcNavigate from '@app/shared/routing/PwcNavigate';
import { uiValuesSlice } from '@app/core/store/ui-values-slice';
import env, { Environment } from '@environment';
import {
  CalcMethod,
  SponsorIrrAndHurdleTab,
} from '@app/shared/models/contracts/enums/shared-enums';
import { useFocusMode } from '@app/shared/hooks/focus-mode';
import { SearchParamKey } from '@app/shared/models/search-param-enum';
import { useLocale } from '@app/core/hooks/useLocale';
import { selectInstruments } from '@app/core/store/capital-structure-slice-selectors';
import {
  getOpmFilteredWaterfallInstruments,
  getPwermFilteredWaterfallInstruments,
} from '@app/modules/projects/outputs/other-calculations/sponsor-irr-and-hurdle/irr-hurdle-calculations/utils/instrumentFilters';
import { shouldUseOpmResultsForCase } from '@app/modules/projects/outputs/other-calculations/sponsor-irr-and-hurdle/irr-hurdle-calculations/hooks/useShouldUseOpmResults';

interface CaseNavigationControlsProps {
  tabPage: SponsorIrrAndHurdleTab;
}

export const CaseNavigationControls = ({ tabPage }: CaseNavigationControlsProps) => {
  const dispatch = useAppDispatch();
  const { l } = useLocale();
  const { caseId } = useParams();
  const [searchParams] = useSearchParams();
  const { isActive, focusType } = useFocusMode();
  const hasFocusParams = Boolean(searchParams.get(SearchParamKey.FocusType));
  const project = useAppSelector((state) => state.project.projectDraft);
  const isOpm = project.details.calcMethod === CalcMethod.OPM;
  const storedSelectedCase = useAppSelector((state) =>
    tabPage === SponsorIrrAndHurdleTab.SponsorIRR
      ? state.uiValues.userSelections.sponsorIrrAndHurdle.sponsorIrr.selectedCase
      : state.uiValues.userSelections.sponsorIrrAndHurdle.irrHurdleCalculations.selectedCase
  );
  const projectInstruments = useAppSelector(selectInstruments);
  const pwermWaterfallResults = useAppSelector(
    (state) => state.pwerm2Calculation.calculatedResults.waterfall
  );
  const opmWaterfallResults = useAppSelector(
    (state) => state.opmCalculation.riskFreeValues.waterfalls
  );

  const cases = project?.pwermInput?.cases || [];

  // the IRR hurdle tab has nothing to show for a case without IRR hurdle results,
  // so those cases are not selectable
  const casesWithoutIrrHurdles = useMemo(() => {
    if (tabPage !== SponsorIrrAndHurdleTab.IRRHurdleCalculations) {
      return new Set<string>();
    }

    const hasIrrHurdleInstruments = (itemCaseId: string) =>
      shouldUseOpmResultsForCase(
        project.details.calcMethod,
        project.opmInput.selectedCaseId,
        itemCaseId,
        opmWaterfallResults,
        pwermWaterfallResults
      )
        ? getOpmFilteredWaterfallInstruments(opmWaterfallResults, projectInstruments).length > 0
        : getPwermFilteredWaterfallInstruments(
            pwermWaterfallResults,
            projectInstruments,
            itemCaseId
          ).length > 0;

    return new Set(
      project.pwermInput.cases
        .filter((item) => !hasIrrHurdleInstruments(item.caseId))
        .map((item) => item.caseId)
    );
  }, [
    tabPage,
    project.pwermInput.cases,
    project.details.calcMethod,
    project.opmInput.selectedCaseId,
    pwermWaterfallResults,
    opmWaterfallResults,
    projectInstruments,
  ]);

  const selectedCase = project.pwermInput.cases.find((item) => item.caseId === caseId);
  const selectedCaseId = selectedCase?.caseId;

  const navigationItems: NavigationItem[] = useMemo(
    () =>
      project.pwermInput.cases
        .map((item) => ({
          label: item.narrative || EmptyValues.EnDash,
          route: ':caseId',
          params: { caseId: item.caseId },
          order: item.caseOrder,
          isHighlighted:
            selectedCaseId === null ? item.caseId === null : item.caseId === selectedCaseId,
          preserveSearch: hasFocusParams,
          isDisabled: casesWithoutIrrHurdles.has(item.caseId),
          disabledReason: l('_NoIRRHurdlesForCase'),
        }))
        .sort((a, b) => a.order! - b.order!) || [],
    [project.pwermInput.cases, selectedCaseId, hasFocusParams, casesWithoutIrrHurdles, l]
  );

  navigationItems.sort((a, b) => a.order! - b.order!);

  useEffect(() => {
    return () => {
      if (caseId) {
        tabPage === SponsorIrrAndHurdleTab.SponsorIRR
          ? dispatch(uiValuesSlice.actions.setSponsorIrrSelectedCase(caseId))
          : dispatch(uiValuesSlice.actions.setIrrHurdleCalculationsSelectedCase(caseId));
      }
    };
  }, [caseId, dispatch, tabPage]);

  const hasUnmountedOnce = useRef(false);

  useEffect(() => {
    return () => {
      if (env.current === Environment.Local && hasUnmountedOnce.current === false) {
        hasUnmountedOnce.current = true;
        return;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isOpm) {
    return null;
  }

  if (!caseId && cases.length > 0) {
    const selectableCases = cases.filter((c) => !casesWithoutIrrHurdles.has(c.caseId));
    const storedCase = selectableCases.find((c) => c.caseId === storedSelectedCase);
    const defaultCase = storedCase ?? selectableCases[0] ?? cases[0];

    return (
      <PwcNavigate
        route=":caseId"
        params={{ caseId: defaultCase.caseId }}
        preserveSearch={hasFocusParams}
      />
    );
  }

  return (
    <div>
      <SecondaryNavigation
        navigationItems={navigationItems}
        isCaseSelector
        variation={SecondaryNavigationVariation.Scrollable}
        focusModeType={isActive ? focusType : undefined}
      />
    </div>
  );
};
